/**
 * ENG-10367 — the tmux kill fan-out, extracted so its CONCURRENCY is a property
 * a test can drive rather than a timing claim in a comment.
 *
 * ## Why this exists
 *
 * `stopPolling()` runs under a hard 15s force-exit backstop. Before ENG-10367
 * the drain fitted inside it by luck rather than by arithmetic, and the card
 * release this ticket adds made the budget worse. The measured worst case, with
 * the kills run SEQUENTIALLY as they were:
 *
 *     tmux list-sessions        2s   (its own timeout)
 *   + kill-session x N          2s each, serial
 *   + release-interrupted       3s   (ENG-10367)
 *   + stopAllSessionsAndWait    4s
 *
 * At N = 3 that is 15s and the backstop fires; at N = 6 it is 21s, and
 * `stopAllSessionsAndWait` never starts at all. A host running six agents is
 * ordinary, so this was not a corner.
 *
 * Killing concurrently collapses `2s x N` to ~2s regardless of N, which puts
 * the worst case at ~11s and leaves real headroom. The kills are independent
 * `tmux kill-session -t <name>` calls against different sessions, so there is
 * no ordering between them to preserve.
 *
 * ## The contract
 *
 * NEVER THROWS, and never rejects for a session that would not die. A drain
 * that fails because one tmux session was already gone is worse than the
 * orphaned pane it was trying to prevent. Per-session outcomes are reported in
 * the return value so the caller can log them, and a failure is counted rather
 * than raised.
 */

export interface SessionKillOutcome {
  /** Sessions whose kill resolved. */
  killed: string[];
  /** Sessions whose kill threw — already exited, or tmux refused. */
  failed: string[];
}

/**
 * Kill every session concurrently, awaiting them all.
 *
 * `killOne` is injected so the concurrency is testable without tmux: a fake
 * that records how many calls are in flight before any resolves proves the fan
 * out is parallel, and reverting this to a serial `for ... await` loop makes
 * that assertion fail. That mutation is the one this function exists to stop.
 */
export async function killSessionsConcurrently(
  sessions: readonly string[],
  killOne: (session: string) => Promise<void>,
): Promise<SessionKillOutcome> {
  const killed: string[] = [];
  const failed: string[] = [];
  if (sessions.length === 0) return { killed, failed };

  const settled = await Promise.allSettled(sessions.map((s) => killOne(s)));
  settled.forEach((r, i) => {
    const session = sessions[i]!;
    if (r.status === 'fulfilled') killed.push(session);
    else failed.push(session);
  });
  return { killed, failed };
}
